import { useRef } from 'react';
import { DebouncedInput } from 'components/inputs/DebounceInput';
import { useTableContext } from '../providers/TableProvider';
import { useKeyPress } from 'hooks/useKeyPress';
import { outlinedInputClasses, styled } from '@mui/material';

const id = 'tableSearchInput';

interface SearchProps {
  searchOpen: boolean;
  setSearchOpen: (value: boolean) => void;
}

export const Search = ({ searchOpen, setSearchOpen }: SearchProps) => {
  const { globalFilter, setGlobalFilter, table, config } = useTableContext();

  const searchRef = useRef<HTMLInputElement>(null);
  const inputValue = table.options.meta?.onTableStateChange
    ? table.options.meta.tableState?.search ?? ''
    : globalFilter ?? '';

  const handleOnChange = (value: string) => {
    if (table.options.meta?.onTableStateChange) {
      table.options.meta.onTableStateChange({
        search: value || undefined,
        page: null,
        cursor: null,
      });
    } else {
      setGlobalFilter(value);
    }
    config?.event?.('searched', null);
  };

  const handleClickAway = () => {
    if (!inputValue) {
      setSearchOpen(false);
      searchRef?.current?.blur();
    }
  };

  const handleFocus = () => {
    searchRef?.current?.focus();
  };

  const onClear = () => {
    setSearchOpen(false);
    handleOnChange('');
    // handleFocus();
  };

  const isSearchOpenOrValue = searchOpen || Boolean(inputValue);
  const onClick = () => {
    if (!isSearchOpenOrValue) {
      setSearchOpen(true);
      handleFocus();
    }
  };

  useKeyPress('ctrl+f', () => {
    if (!searchOpen) {
      setSearchOpen(true);
      handleFocus();
    }
  });

  useKeyPress('Escape', () => {
    if (searchOpen && !inputValue) {
      onClear();
    }
    searchRef?.current?.blur();
  });

  return (
    <StyledDebouncedInput
      ref={searchRef}
      data-testid="searchBtn"
      id={id}
      isSearchOpenOrValue={isSearchOpenOrValue}
      value={inputValue}
      onChange={handleOnChange}
      placeholder="Search"
      onClear={onClear}
      onBlur={handleClickAway}
      onClick={onClick}
    />
  );
};

const StyledDebouncedInput = styled(DebouncedInput)<{
  isSearchOpenOrValue?: boolean;
}>(({ theme, isSearchOpenOrValue }) => ({
  '&.MuiFormControl-root': {
    svg: {
      minWidth: '20px',
      height: '20px',
    },
    input: { paddingLeft: 'unset' },
    maxWidth: '32px',
    paddingInline: '4px',
    transition: theme.transitions.create('max-width'),
    ...(isSearchOpenOrValue
      ? {
          maxWidth: '100%',
        }
      : {}),
    '&:focus-within': {
      maxWidth: '100%',
    },
    [`& .${outlinedInputClasses.root}`]: {
      transition: 'background-color 1000ms ease-out',

      ...(!isSearchOpenOrValue
        ? { paddingLeft: 0, backgroundColor: 'transparent', cursor: 'pointer' }
        : {}),
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        transition: 'border 1000ms ease-out, box-shadow 1000ms ease-out',
        ...(!isSearchOpenOrValue
          ? {
              border: 'none',
            }
          : {}),
      },
      [`&.${outlinedInputClasses.focused}`]: {
        paddingLeft: 14,
      },
    },
  },
}));
